import { auth } from './firebase';
import { config } from '@/config';
import type {
  WebsiteListResponse,
  WebsiteResponse,
  ArticleListResponse,
  ArticleResponse,
  DashboardResponse,
  ArticleReportResponse,
  CreateWebsiteInput,
  UpdateWebsiteInput,
  UpdateArticleInput,
  CrawlJobResponse,
} from '@/types/api';

class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError(401, 'Not authenticated');
  }
  return user.getIdToken();
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const response = await fetch(`${config.apiBaseUrl}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }

  return response.json();
}

export const api = {
  // Websites - using Firebase Hosting proxy paths
  async listWebsites(): Promise<WebsiteListResponse> {
    return fetchWithAuth<WebsiteListResponse>('/websites/');
  },

  async getWebsite(websiteId: string): Promise<WebsiteResponse> {
    return fetchWithAuth<WebsiteResponse>(`/websites/${websiteId}`);
  },

  async createWebsite(data: CreateWebsiteInput): Promise<WebsiteResponse> {
    return fetchWithAuth<WebsiteResponse>('/websites/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateWebsite(websiteId: string, data: UpdateWebsiteInput): Promise<WebsiteResponse> {
    return fetchWithAuth<WebsiteResponse>(`/websites/${websiteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteWebsite(websiteId: string): Promise<void> {
    await fetchWithAuth(`/websites/${websiteId}`, {
      method: 'DELETE',
    });
  },

  async verifyDomain(websiteId: string, method: 'dns' | 'meta' | 'file'): Promise<WebsiteResponse> {
    return fetchWithAuth<WebsiteResponse>(`/websites/${websiteId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ method }),
    });
  },

  async startCrawl(
    websiteId: string,
    force = false
  ): Promise<{ success: boolean; job: CrawlJobResponse } | { error: string; activeJob: CrawlJobResponse }> {
    const token = await getAuthToken();
    const response = await fetch(`${config.apiBaseUrl}/crawler/manual`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ websiteId, force }),
    });

    const data = await response.json();

    if (response.status === 409) {
      // Conflict - active job exists
      return { error: data.error, activeJob: data.activeJob };
    }

    if (!response.ok) {
      throw new ApiError(response.status, data.error || 'Request failed');
    }

    return data;
  },

  async getCrawlJobStatus(jobId: string): Promise<CrawlJobResponse> {
    return fetchWithAuth<CrawlJobResponse>(`/crawler/status/${jobId}`);
  },

  async cancelCrawlJob(jobId: string): Promise<{ success: boolean; message: string }> {
    return fetchWithAuth<{ success: boolean; message: string }>(`/crawler/cancel/${jobId}`, {
      method: 'POST',
    });
  },

  async getActiveCrawlJob(websiteId: string): Promise<{ activeJob: CrawlJobResponse | null }> {
    return fetchWithAuth<{ activeJob: CrawlJobResponse | null }>(`/crawler/active/${websiteId}`);
  },

  async checkSitemap(websiteId: string): Promise<{
    hasChanges: boolean;
    newUrls: string[];
    updatedUrls: { url: string; lastmod: string | null }[];
    totalInSitemap: number;
    totalExisting: number;
  }> {
    return fetchWithAuth(`/crawler/check-sitemap/${websiteId}`);
  },

  async crawlAndAnalyze(
    websiteId: string,
    urls: string[]
  ): Promise<{
    success: boolean;
    results: {
      processed: number;
      created: number;
      updated: number;
      analyzed: number;
      failed: number;
    };
  }> {
    return fetchWithAuth(`/crawler/crawl-and-analyze`, {
      method: 'POST',
      body: JSON.stringify({ websiteId, urls }),
    });
  },

  async regenerateBadgeKey(websiteId: string): Promise<{ badgeApiKey: string }> {
    return fetchWithAuth<{ badgeApiKey: string }>(`/websites/${websiteId}/regenerate-key`, {
      method: 'POST',
    });
  },

  // Articles - using Firebase Hosting proxy paths
  async listArticles(
    websiteId: string,
    params?: {
      limit?: number;
      startAfter?: string;
      enabled?: boolean;
      sortBy?: 'createdAt' | 'publishedAt' | 'actualRating' | 'visitorUpvote' | 'visitorDownvote' | 'title';
      sortOrder?: 'asc' | 'desc';
      minRating?: number;
      maxRating?: number;
      minUpvote?: number;
      minDownvote?: number;
      dateFrom?: string;
      dateTo?: string;
    }
  ): Promise<ArticleListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.startAfter) searchParams.set('startAfter', params.startAfter);
    if (params?.enabled !== undefined) searchParams.set('enabled', String(params.enabled));
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params?.minRating !== undefined) searchParams.set('minRating', String(params.minRating));
    if (params?.maxRating !== undefined) searchParams.set('maxRating', String(params.maxRating));
    if (params?.minUpvote !== undefined) searchParams.set('minUpvote', String(params.minUpvote));
    if (params?.minDownvote !== undefined) searchParams.set('minDownvote', String(params.minDownvote));
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);

    const query = searchParams.toString();
    return fetchWithAuth<ArticleListResponse>(
      `/articles/${websiteId}/articles${query ? `?${query}` : ''}`
    );
  },

  async getArticle(websiteId: string, articleId: string): Promise<ArticleResponse> {
    return fetchWithAuth<ArticleResponse>(`/articles/${websiteId}/articles/${articleId}`);
  },

  async updateArticle(
    websiteId: string,
    articleId: string,
    data: UpdateArticleInput
  ): Promise<ArticleResponse> {
    return fetchWithAuth<ArticleResponse>(
      `/articles/${websiteId}/articles/${articleId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  },

  async deleteArticle(websiteId: string, articleId: string): Promise<void> {
    await fetchWithAuth(`/articles/${websiteId}/articles/${articleId}`, {
      method: 'DELETE',
    });
  },

  // Reports - using Firebase Hosting proxy paths
  async getDashboard(websiteId: string): Promise<DashboardResponse> {
    return fetchWithAuth<DashboardResponse>(`/reports/dashboard/${websiteId}`);
  },

  async getArticleReport(articleId: string): Promise<ArticleReportResponse> {
    return fetchWithAuth<ArticleReportResponse>(`/reports/article/${articleId}`);
  },
};

export { ApiError };
